import { GoogleOAuthProvider } from '@react-oauth/google'
import { observer } from 'mobx-react'
import React, { useEffect, useRef, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { Link, Route, Routes, useLocation } from 'react-router-dom'
import styled, { createGlobalStyle } from 'styled-components'
import { PrimaryButton, SecondaryButton, IconButton } from './components/Button'
import Dropdown, { DropdownItem } from './components/Dropdown'
import { InputLabel, TextInput } from './components/Input'
import InviteAcceptModal from './components/InviteAcceptModal'
import ConvertDrawer from './components/ConvertDrawer'
import OpenBankingPaymentDrawer from './components/OpenBankingPaymentDrawer'
import RadomV2Banner from './components/RadomV2Banner'
import Modal, { ModalFooter } from './components/Modal'
import SearchBox from './components/SearchBox'
import Tooltip from './components/Tooltip'
import WithAuthentication from './components/WithAuthentication'
import { Chevron } from './icons/Chevron'
import { HoverInfoIcon } from './icons/Info'
import AnalyticsIcon from './icons/nav/Analytics'
import CustomersIcon from './icons/nav/Customers'
import HomeIcon from './icons/nav/Home'
import ProductsIcon from './icons/nav/Products'
import SettingsIcon from './icons/nav/Settings'
import StatementsIcon from './icons/nav/Statements'
import SupportNavIcon from './icons/nav/Support'
import AddMoneyIcon from './icons/nav/AddMoney'
import SendMoneyIcon from './icons/nav/SendMoney'
import BatchPayoutsIcon from './icons/nav/BatchPayouts'
import AutomationsIcon from './icons/nav/Automations'
import ConvertNavIcon from './icons/nav/Convert'
import PaymentLinkIcon from './icons/nav/PaymentLink'
import MerchantIcon from './icons/nav/Merchant'
import CheckoutSessionIcon from './icons/nav/CheckoutSession'
import InvoiceIcon from './icons/nav/Invoice'
import SubscriptionsIcon from './icons/nav/Subscriptions'
import WalletNavIcon from './icons/nav/WalletNav'
import WebhooksNavIcon from './icons/nav/WebhooksNav'
import ApiKeyIcon from './icons/nav/ApiKey'
import TestnetBoxIcon from './icons/nav/TestnetBox'
import RadomLogo from './icons/Radom'
import { Menu } from './icons/Menu'
import EventsState from './state/Events'
import NavbarController from './state/NavbarController'
import Radom, { IOrganization } from './state/Radom'
import Treasury from './state/Treasury'
import User from './state/User'
import { GOOGLE_OAUTH_CLIENT_ID, RADOM_COLORS } from './util/Constants'
import { errorToast, successToast, useTestMode, ROUTES } from './util/Util'
import Subscriptions from './views/Billing/Subscriptions'
import Transactions from './views/Transactions'
import CheckoutSession from './views/CheckoutSession'
import CheckoutSessions from './views/CheckoutSessions'
import ContactsDirectory from './views/Customers/ContactsDirectory'
import CustomerView from './views/Customers/Customer'
import APITokens from './views/Developers/APITokens'
import Developers from './views/Developers/Developers'
import TestnetTokens from './views/Developers/TestnetTokens'
import ViewWebhook from './views/Developers/ViewWebhook'
import Webhooks from './views/Developers/Webhooks'
import DiscountCodes from './views/DiscountCodes'
import Funds from './views/Funds'
import Accounts from './views/Funds/Accounts'
import AutomationView from './views/Funds/Automations'
import AnalyticsView from './views/Analytics'
import Home from './views/Home'
import Invoices from './views/Invoices'
import CreateInvoice from './views/Invoices/CreateInvoice'
import ViewInvoice from './views/Invoices/ViewInvoice'
import NotFound from './views/NotFound'
import Profile from './views/Organization/Overview'
import Payment from './views/Payment'
import PaymentLinks from './views/PaymentLinks'
import { CreatePaymentLink } from './views/PaymentLinks/CreatePaymentLink'
import EditPaymentLink from './views/PaymentLinks/EditPaymentLink'
import Products from './views/Products'
import { CreateDiscount } from './views/Products/CreateDiscountCode'
import CreateProduct from './views/Products/CreateProduct'
import EditProduct from './views/Products/EditProduct'
import Subscription from './views/Subscription'
import CreateAPIRole from './views/Team/CreateAPIRole'
import Verify from './views/Verify'
import Deposit from './views/Funds/Deposit'
import Funding from './views/Funds/Funding'
import { Refund as RefundView } from './views/Refund'
import Withdrawal from './views/Withdrawal'
import SwapView from './views/Swap'
import Settings from './views/Settings'
import Statements from './views/Statements'
import Support from './views/Support'
import SupportTicket from './views/SupportTicket'
import ViewPaymentLink from './views/PaymentLinks/ViewPaymentLink'
import DepositAddresses from './views/DepositAddresses'
import Spinner from './components/Spinner'
import Close from './icons/Close'
import Checkmark from './icons/Checkmark'
import Copy from './icons/Copy'
import VirtualAccounts from './views/VirtualAccounts'
import MassivePayouts from './views/MassivePayouts'
import CreateMassivePayout from './views/Funds/CreateMassivePayout'
import MassivePayout from './views/MassivePayout'
import Payouts from './views/Payouts'
import CreatePayout from './views/Funds/CreatePayout'
import Payout from './views/Payout'
import Send from './views/Send/Send'
import BatchPayouts from './views/Send/BatchPayouts'
import CurrencyPage from './views/Money/CurrencyPage'
import MoneyHome from './views/Money/MoneyHome'
import TransactionNavbarIcon from './icons/nav/TransactionNavbarIcon'
import NotificationDropdown from './components/NotificationDropdown/NotifiactionDropdown'
import HelpDropdown from './components/HelpDropdown/HelpDropdown'
import RequirePermission from './components/RequirePermission'
import { PermissionRequirement, satisfiesPermission } from './util/permissions'
import Convert from './views/Convert/Convert'

const GlobalStyles = createGlobalStyle<{ isModalOpen: boolean }>`
  body {
    ${({ isModalOpen }) => isModalOpen && 'overflow: hidden;'}
  }
`

const RadomNavContainer = styled.div<{ $isHidden: boolean, $isMobile: boolean }>`
  position: fixed;
  display: flex;
  flex-direction: column;
  z-index: ${({ $isMobile }) => $isMobile ? 6 : 3};
  transition: 0.3s ease all;
  height: 100vh;
  justify-content: space-between;
  border-right: 1px solid var(--surface-pressed, #edf1f7);
  box-sizing: border-box;
  width: 228px;
  background: var(--surface, #ffffff);
  transform: translateX(${({ $isHidden }) => $isHidden ? '-400px' : '0'});
  overflow: hidden;

  @media (max-width: 900px) {
    width: 80vw;
    max-width: 260px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`

const RadomNav = styled.div`
  transition: 0.2s ease all;
  background: var(--surface, #ffffff);
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const NavRadomLogo = styled(Link)`
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
  text-decoration: none;

  :hover {
    opacity: 0.9;
  }

  :active {
    opacity: 1;
  }
`

const NavItem = styled(Link) <{ $$active: boolean }>`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  height: auto;
  font-size: 14px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  padding: 0;
  user-select: none;
  text-decoration: none;
  width: 100%;
  white-space: normal;
  ${({ $$active }) => $$active && `color: ${RADOM_COLORS.BLACK};`}
  ${({ $$active }) => $$active && `
    svg {
      stroke: ${RADOM_COLORS.BLACK};
    }
  `}

  svg {
    transition: 0.2s ease all;
  }

  :hover {
    opacity: 1;

    svg {
      stroke: ${RADOM_COLORS.BLACK};
    }
  }

  :active > div {
    transform: scale(0.985);
  }
`

const NavSectionLabel = styled.div`
  flex: 0 0 auto;
  padding: 16px 11px 5px;
  color: var(--text-faint, #9aa0ae);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  user-select: none;
`

const NavDivider = styled.div`
  background: var(--hairline, #eef1f5);
  flex: 0 0 auto;
  height: 1px;
  margin: 10px 9px 6px;
`

const ThemeGlyph = ({ dark }: { dark: boolean }): React.ReactElement => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    {dark
      ? <>
        <circle cx='12' cy='12' r='5' />
        <path d='M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42' />
      </>
      : <path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' />}
  </svg>
)

const NavGroupHeader = styled.div<{ $active: boolean, $open: boolean }>`
  align-items: center;
  flex: 0 0 auto;
  background: ${({ $active }) => $active ? 'var(--nav-active-bg, #f3f4f6)' : 'transparent'};
  border: 1px solid ${({ $active }) => $active ? 'var(--nav-active-border, #eceff3)' : 'transparent'};
  border-radius: 10px;
  box-sizing: border-box;
  color: ${({ $active }) => $active ? RADOM_COLORS.BLACK : 'var(--text-muted, #566071)'};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  gap: 7px;
  justify-content: space-between;
  line-height: 1.34;
  min-height: 33px;
  padding: 7px 11px;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.12s ease;
  user-select: none;
  width: 100%;

  svg {
    transition: 0.2s ease all;
  }

  ${({ $active }) => $active && `
    svg {
      stroke: ${RADOM_COLORS.BLACK};
    }
  `}

  &:hover {
    ${({ $active }) => !$active && 'background: var(--nav-hover-bg, #f7f7f8);'}

    svg {
      stroke: ${RADOM_COLORS.BLACK};
    }

    #chevron {
      opacity: 0.88;
    }
  }

  &:active {
    transform: scale(0.985);
  }

  #chevron {
    opacity: 0.42;
    stroke: none;
    transform: rotate(${({ $open }) => $open ? '-180deg' : '0deg'});
    transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.18s ease;
  }
`

const NavGroupChildren = styled.div<{ $open: boolean }>`
  display: flex;
  /* The nav is a shrinkable flex column; without this the open group gets
     crushed to a few pixels and its children vanish under overflow:hidden. */
  flex: 0 0 auto;
  flex-direction: column;
  gap: 3px;
  max-height: ${({ $open }) => $open ? '420px' : '0'};
  opacity: ${({ $open }) => $open ? 1 : 0};
  overflow: hidden;
  padding-bottom: ${({ $open }) => $open ? '5px' : '0'};
  padding-top: ${({ $open }) => $open ? '4px' : '0'};
  pointer-events: ${({ $open }) => $open ? 'auto' : 'none'};
  transform: translateY(${({ $open }) => $open ? '0' : '-4px'});
  transition:
    max-height 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.2s ease,
    transform 0.24s ease,
    padding 0.24s ease;
  width: 100%;
`

const NavItemText = styled.div<{ $active: boolean }>`
  padding: 7px 11px;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.12s ease;
  border-radius: 10px;
  box-sizing: border-box;
  border: 1px solid transparent;
  background: transparent;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--text-muted, #566071);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.34;
  min-height: 33px;
  white-space: normal;

  ${({ $active }) => $active && `
    background: var(--nav-active-bg, #f3f4f6);
    color: ${RADOM_COLORS.BLACK};
    border-color: var(--nav-active-border, #eceff3);
  `}

  &:hover {
    ${({ $active }) => !$active && 'background: var(--nav-hover-bg, #f7f7f8);'}
  }
`

const UnreadPip = styled.span`
  margin-left: auto;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: 8px;
  background: ${RADOM_COLORS.ERROR};
  color: white;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
`

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 5;
  transition: opacity 0.2s ease;

  @media (min-width: 901px) {
    display: none;
  }
`

const SelectedTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--surface-pressed, #f3f4f6);
  border: 1px solid var(--border, #e7eaf0);
  color: ${RADOM_COLORS.BLACK};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
`

const OrganizationSectionLabel = styled.div`
  color: var(--text-faint, #8a91a0);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  padding: 6px 0 4px;
  text-transform: uppercase;
`

const OrganizationSectionDivider = styled.div`
  border-top: 1px solid var(--hairline, #eef1f5);
  margin: 8px 12px 0;
`

const OrganizationModalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const OrganizationRowShell = styled.div`
  width: 100%;
`

const OrganizationRowButton = styled.div<{
  $selected?: boolean
  $nested?: boolean
  $disabled?: boolean
  $context: 'dropdown' | 'modal'
}>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  min-height: ${({ $context, $nested }) => {
    if ($context === 'modal') {
      return $nested ? '54px' : '60px'
    }

    return $nested ? '46px' : '52px'
  }};
  padding: ${({ $context, $nested }) => {
    if ($context === 'modal') {
      return $nested ? '10px 12px' : '11px 12px'
    }

    return $nested ? '8px 12px' : '9px 12px'
  }};
  background: ${({ $selected }) => $selected ? 'var(--surface-hover, #f6f7f9)' : 'transparent'};
  border: 1px solid ${({ $selected }) => $selected ? 'var(--border, #e7eaf0)' : 'transparent'};
  border-radius: 12px;
  gap: 12px;
  cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
  text-align: left;
  transition: 0.16s ease background-color, 0.16s ease color, 0.16s ease border-color;

  &:hover {
    background: ${({ $selected, $disabled }) => $disabled
    ? ($selected ? 'var(--surface-hover, #f6f7f9)' : 'transparent')
    : ($selected ? 'var(--surface-hover, #f1f3f6)' : 'var(--surface-hover, #f7f8fa)')};
    border-color: ${({ $selected, $disabled }) => $disabled
    ? ($selected ? 'var(--border, #e7eaf0)' : 'transparent')
    : 'var(--border, #e7eaf0)'};
  }

  opacity: ${({ $disabled }) => $disabled ? 0.65 : 1};
`

const OrganizationRowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`

const OrganizationRowTitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
`

const OrganizationRowTitle = styled.span<{ $context: 'dropdown' | 'modal', $allowWrap?: boolean }>`
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: ${({ $context }) => $context === 'modal' ? '14.5px' : '13.25px'};
  font-weight: ${({ $context }) => $context === 'modal' ? 520 : 470};
  line-height: 1.35;
  flex: 1;
  overflow: ${({ $allowWrap }) => $allowWrap ? 'visible' : 'hidden'};
  text-overflow: ${({ $allowWrap }) => $allowWrap ? 'clip' : 'ellipsis'};
  white-space: ${({ $allowWrap }) => $allowWrap ? 'normal' : 'nowrap'};
`

const OrganizationRelationLabel = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid var(--border, #e7eaf0);
  background: var(--surface-hover, #f7f8fa);
  color: var(--text-faint, #7d8594);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`

const OrganizationRowMeta = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`

const OrganizationRowId = styled.span<{ $context: 'dropdown' | 'modal' }>`
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-family: var(--font-mono);
  font-size: ${({ $context }) => $context === 'modal' ? '11.85px' : '11.35px'};
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const OrganizationInlineCopyButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--text-faint, #8a91a0);
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 22px;
  justify-content: center;
  padding: 0;
  transition: 0.16s ease background-color, 0.16s ease color;
  width: 22px;

  &:hover {
    background: var(--hairline, #eef1f5);
    color: ${RADOM_COLORS.BLACK};
  }
`

const OrganizationRowHint = styled.span`
  color: var(--text-faint, #8a91a0);
  font-size: 11.75px;
  line-height: 1.45;
`

const OrganizationRowTrailing = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 2px;
`

const OrganizationTick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  color: ${RADOM_COLORS.BLACK};
`

const OrganizationChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 24px;
  padding-left: 18px;
  border-left: 1px solid var(--border, #dde4ee);
`

const ModePill = styled.span<{ $test?: boolean }>`
  align-items: center;
  background: ${({ $test }) => $test ? 'var(--surface-pressed, #f3f4f6)' : 'transparent'};
  border: 1px solid var(--border, #e7eaf0);
  border-radius: 999px;
  color: var(--text-muted, #5f6675);
  display: inline-flex;
  justify-content: center;
  padding: 3px 7px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
`

const ModeToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  border: 1px solid var(--border, #e7eaf0);
  background: var(--surface-hover, #f7f8fa);
`

const ModeToggleButton = styled.button<{ $active?: boolean }>`
  min-width: 58px;
  height: 28px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'transparent'};
  box-shadow: ${({ $active }) => $active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? RADOM_COLORS.BLACK : 'var(--text-faint, #7b8392)'};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: 0.16s ease background-color, 0.16s ease color, 0.16s ease box-shadow;

  &:hover {
    color: ${RADOM_COLORS.BLACK};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: ${({ $active }) => $active ? 1 : 0.55};
  }
`

const OrganizationTreeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const OrganizationDropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 13px;
  width: 100%;
  min-height: 0;
  max-height: inherit;
`

const OrganizationDropdownBody = styled.div`
  width: 100%;
  padding: 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`

const OrganizationDropdownActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px 0;
`

const OrganizationDropdownActionLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
`

const OrganizationDropdownActionHint = styled.span`
  color: var(--text-faint, #8a91a0);
  font-size: 11.5px;
  line-height: 1.45;
`

const OrganizationDropdownFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 8px 8px;
  margin-top: 0;
  background: var(--surface, #ffffff);
  border-top: 1px solid var(--hairline, #eef1f5);
  flex: 0 0 auto;
`

const AppBlocker = styled.div`
  position: fixed;
  inset: 0;
  z-index: 130;
  background: var(--blocker-scrim, rgba(255, 255, 255, 0.72));
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`

const AppBlockerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: min(360px, calc(100vw - 32px));
  max-width: min(420px, calc(100vw - 32px));
  padding: 18px 20px;
  border-radius: 20px;
  border: 1px solid var(--border-soft, #eceff3);
  background: var(--blocker-card-bg, rgba(255, 255, 255, 0.96));
  box-shadow: 0 28px 70px var(--shadow-color, rgba(15, 23, 42, 0.14));
`

const RadomBottomNav = styled.div`
  flex-shrink: 0;
  padding: 8px 14px 26px;
  border-top: 1px solid var(--surface-pressed, #edf1f7);
`

interface RouteConfig {
  path?: string
  name?: string
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
  element?: React.ReactNode
  children?: RouteConfig[]
  requireAPI?: boolean
  features?: string[]
  isBottomNav?: boolean
  isExternal?: boolean
  permission?: PermissionRequirement
  // Nav grouping: consecutive entries sharing a section render under one
  // small-caps label. Entries without a section form the top cluster.
  section?: string
  // Renders a hairline divider above this nav entry.
  dividerAbove?: boolean
  testModeDisabled?: boolean
}

function isRouteAccessible (route: RouteConfig, testMode: boolean, org?: IOrganization): boolean {
  if (route.testModeDisabled && testMode) {
    return false
  }

  if (!route.features) {
    return true
  }

  return route.features.every(feature => !!org?.features?.[feature as keyof NonNullable<IOrganization['features']>])
}

const routes: RouteConfig[] = [
  {
    // Banking-first money home: balances, accounts, and receive details in
    // one screen. The previous overview stays reachable at /overview.
    path: '/',
    name: 'Home',
    icon: HomeIcon,
    element: <MoneyHome />
  },
  {
    path: '/overview',
    element: <Home />
  },
  {
    // One page per currency (crypto ticker or EUR/USD/GBP cash), linked from
    // the Money home rows.
    path: '/money/:code',
    element: <CurrencyPage />
  },
  {
    path: '/analytics',
    name: 'Analytics',
    icon: AnalyticsIcon,
    element: <AnalyticsView />,
    permission: 'ReadEvents'
  },
  // ——— Money: the org's own funds moving in and out. Balances and saved
  // accounts management live inside Home and Send rather than the nav.
  {
    // Receive-side counterpart to Send money: the Funding workspace opened on
    // its deposit flow (bank details and crypto deposit addresses).
    path: '/receive',
    name: 'Add funds',
    dividerAbove: true,
    icon: AddMoneyIcon,
    element: <Funding receiveOnly />,
    permission: { anyOf: ['ReadDeposit', 'ReadDepositAccount'] }
  },

  {
    // Unified destination-first flow replacing the separate Withdraw/Payout
    // entry points. The old /payouts and /mass_payouts routes stay registered
    // below for deep links.
    path: '/send',
    name: 'Send funds',
    icon: SendMoneyIcon,
    element: <Send />,
    permission: { anyOf: ['WriteWithdrawal', 'WritePayouts', 'ReadPayouts', 'ReadWithdrawal'] }
  },
  {
    path: '/convert',
    name: 'Convert',
    icon: ConvertNavIcon,
    element: <Convert />,
    features: ['isConversionEnabled', 'isUnifiedTreasuryEnabled'],
    testModeDisabled: true
  },
  {
    // Batch is its own product surface (affiliate payouts, creator payments,
    // payroll) rather than a mode of Send.
    path: '/batch',
    name: 'Batch payouts',
    icon: BatchPayoutsIcon,
    element: <BatchPayouts />,
    permission: { anyOf: ['WritePayouts', 'ReadPayouts'] }
  },
  {
    // One directory for everyone money moves between: the org's own accounts
    // pinned on top, then every contact with their ways to pay, each one
    // click from Send. Contacts also arrive here from incoming payments.
    path: '/customers',
    name: 'Contacts',
    icon: CustomersIcon,
    element: <ContactsDirectory />,
    permission: { anyOf: ['ReadCustomers', 'ReadExternalAccount', 'ReadWithdrawalAccount'] }
  },
  {
    // The org's own wallets and bank accounts: where its money goes when it
    // withdraws to itself. Kept apart from Contacts, which are other people.
    // Reached from the "You" row in Contacts and from Send funds.
    path: '/accounts',
    element: <Accounts />,
    permission: 'ReadWithdrawalAccount'
  },
  {
    // Older links to the recipients directory and the contacts list.
    path: '/recipients',
    element: <ContactsDirectory />,
    permission: { anyOf: ['ReadCustomers', 'ReadExternalAccount', 'ReadWithdrawalAccount'] }
  },
  {
    path: '/contacts',
    element: <ContactsDirectory />,
    permission: { anyOf: ['ReadCustomers', 'ReadExternalAccount', 'ReadWithdrawalAccount'] }
  },
  {
    path: '/transactions',
    name: 'Transactions',
    icon: TransactionNavbarIcon,
    element: <Transactions />,
    permission: 'ReadEvents'
  },
  {
    // Monthly statements and requested reports: the documents inbox.
    path: '/statements',
    name: 'Statements',
    icon: StatementsIcon,
    element: <Statements />,
    permission: 'ReadEvents'
  },
  {
    path: '/automation',
    name: 'Automations',
    icon: AutomationsIcon,
    element: <AutomationView />,
    permission: 'ReadAutomation'
  },
  // ——— The acquiring side folds into one sub-menu: every way customers pay.
  {
    name: 'Merchant',
    icon: MerchantIcon,
    dividerAbove: true,
    children: [
      {
        path: '/payment_links',
        name: 'Payment links',
        icon: PaymentLinkIcon,
        element: <PaymentLinks />,
        requireAPI: true,
        permission: 'ReadPaymentLinks'
      },
      {
        path: '/checkout_sessions',
        name: 'Checkout sessions',
        icon: CheckoutSessionIcon,
        element: <CheckoutSessions />,
        requireAPI: true,
        permission: 'ReadCheckoutSessions'
      },
      {
        path: '/invoices',
        name: 'Invoices',
        icon: InvoiceIcon,
        element: <Invoices />,
        requireAPI: true,
        permission: 'ReadInvoices'
      },
      {
        path: '/subscriptions',
        name: 'Subscriptions',
        icon: SubscriptionsIcon,
        element: <Subscriptions />,
        permission: 'ReadSubscriptions'
      },
      {
        path: '/deposit_addresses',
        name: 'Deposit addresses',
        icon: WalletNavIcon,
        element: <DepositAddresses />,
        permission: 'ReadDepositAccount'
      }
    ]
  },
  // Discount codes live as a tab inside Products, so the nav stays lean.
  {
    path: '/products',
    name: 'Products',
    icon: ProductsIcon,
    element: <Products />,
    requireAPI: true,
    permission: 'ReadProducts'
  },
  {
    name: 'Developers',
    icon: ApiKeyIcon,
    children: [
      {
        path: '/developers/webhooks',
        name: 'Webhooks',
        icon: WebhooksNavIcon,
        element: <Webhooks />,
        requireAPI: true,
        permission: 'ReadWebhooks'
      },
      {
        path: '/developers/api_tokens',
        name: 'API tokens',
        icon: ApiKeyIcon,
        element: <Developers />,
        requireAPI: true,
        permission: 'ReadAPITokens'
      },
      {
        path: '/developers/testnet_tokens',
        name: 'Testnet tokens',
        icon: TestnetBoxIcon,
        element: <TestnetTokens />,
        requireAPI: true
      }
    ]
  },
  {
    path: '/support',
    name: 'Support',
    icon: SupportNavIcon,
    element: <Support />,
    isBottomNav: true
  },
  {
    path: '/support/:id',
    element: <SupportTicket />
  },
  {
    path: '/settings',
    name: 'Settings',
    icon: SettingsIcon,
    element: <Settings />,
    isBottomNav: true
  },
  {
    path: '/deposit_addresses',
    element: <DepositAddresses />
  },
  {
    path: '/funding',
    element: <Funding />
  },
  {
    path: '/virtual_accounts',
    element: <VirtualAccounts />
  },
  {
    path: '/invoices',
    element: <Invoices />,
    requireAPI: true
  },
  {
    path: '/checkout_sessions',
    element: <CheckoutSessions />,
    requireAPI: true
  },
  {
    path: '/payment_links',
    element: <PaymentLinks />,
    requireAPI: true
  },
  {
    path: '/funds',
    element: <Funds />,
    permission: 'ReadBalance'
  },
  {
    path: '/subscriptions',
    element: <Subscriptions />
  },
  {
    path: '/products',
    element: <Products />,
    requireAPI: true
  },
  {
    path: '/discount_codes',
    element: <DiscountCodes />,
    requireAPI: true
  },
  {
    path: '/deposit/:id',
    element: <Deposit />,
    permission: 'ReadDeposit'
  },
  {
    path: '/refund/:id',
    element: <RefundView />,
    permission: 'ReadRefunds'
  },
  {
    path: ROUTES.MASSIVE_PAYOUTS,
    element: <MassivePayouts />,
    permission: 'ReadPayouts'
  },
  {
    path: ROUTES.CREATE_MASSIVE_PAYOUT,
    element: <CreateMassivePayout />,
    permission: 'WritePayouts'
  },
  {
    path: ROUTES.PAYOUTS,
    element: <Payouts />,
    permission: 'ReadPayouts'
  },

  {
    path: ROUTES.CREATE_PAYOUT,
    element: <CreatePayout />,
    permission: ['WritePayouts', 'WriteConfirmPayouts', 'ReadCustomers', 'ReadExternalAccount']
  },
  {
    path: '/automation',
    element: <AutomationView />
  },
  {
    path: '/invoices/create',
    element: <CreateInvoice />,
    requireAPI: true,
    permission: 'WriteInvoices'
  },
  {
    path: '/products/create',
    element: <CreateProduct />,
    requireAPI: true,
    permission: 'WriteProducts'
  },
  {
    path: '/products/:productId',
    element: <EditProduct />,
    requireAPI: true,
    permission: 'WriteProducts'
  },
  {
    path: '/discounts/create',
    element: <CreateDiscount />,
    requireAPI: true,
    permission: ['WriteDiscountCodes', 'ReadProducts']
  },
  {
    path: '/payment_links/create',
    element: <CreatePaymentLink />,
    requireAPI: true,
    permission: 'WritePaymentLinks'
  },
  {
    path: '/payment_links/:paymentLinkId',
    element: <EditPaymentLink />,
    requireAPI: true,
    permission: 'WritePaymentLinks'
  },
  {
    path: '/payment_link/detail/:paymentLinkId',
    element: <ViewPaymentLink />,
    requireAPI: true,
    permission: 'ReadPaymentLinks'
  },
  {
    path: '/customer/:id',
    element: <CustomerView />,
    requireAPI: true,
    permission: 'ReadCustomers'
  },
  {
    path: '/invoices/:invoiceId',
    element: <ViewInvoice />,
    requireAPI: true,
    permission: 'ReadInvoices'
  },
  {
    path: '/role/create',
    element: <CreateAPIRole />,
    permission: 'WriteRoles'
  },
  {
    path: '/role/:id/edit',
    element: <CreateAPIRole />,
    permission: ['WriteRoles', 'ReadRoles']
  },
  {
    path: '/developers/webhook/:webhookId',
    element: <ViewWebhook />,
    requireAPI: true,
    permission: 'ReadWebhooks'
  },
  {
    path: '/profile',
    element: <Profile />,
    requireAPI: true
  },
  {
    path: '/payment/:id',
    element: <Payment />,
    permission: 'ReadEvents'
  },
  {
    path: '/withdrawal/:id',
    element: <Withdrawal />,
    permission: 'ReadWithdrawal'
  },
  {
    path: '/swap/:id',
    element: <SwapView />,
    permission: 'ReadSwap'
  },
  {
    path: ROUTES.MASSIVE_PAYOUT_DETAIL,
    element: <MassivePayout />,
    permission: 'ReadPayouts'
  },
  {
    path: ROUTES.PAYOUT_DETAIL,
    element: <Payout />,
    permission: 'ReadPayouts'
  },
  {
    path: '/checkout_session/:id',
    element: <CheckoutSession />,
    permission: 'ReadCheckoutSessions'
  },
  {
    path: '/developers/webhooks',
    element: <Webhooks />
  },
  {
    path: '/developers/api_tokens',
    element: <APITokens />
  },
  {
    path: '/developers/testnet_tokens',
    element: <TestnetTokens />
  },
  {
    path: '/subscription/:subscriptionId',
    element: <Subscription />,
    requireAPI: true,
    permission: 'ReadSubscriptions'
  },
  {
    path: '/verify',
    element: <Verify />
  }
]

const routePermissions = new Map<string, PermissionRequirement>()
const indexRoutePermissions = (routeConfigs: RouteConfig[]): void => {
  for (const route of routeConfigs) {
    if (route.path && route.permission !== undefined && !routePermissions.has(route.path)) {
      routePermissions.set(route.path, route.permission)
    }
    if (route.children) {
      indexRoutePermissions(route.children)
    }
  }
}
indexRoutePermissions(routes)

const IMPERSONATION_ALLOWED_ORG_IDS = new Set([
  '00000000-0000-0000-0000-000000000000',
  '00000000-0000-0000-0000-000000000999'
])
const IMPERSONATION_ORG_ID_KEY = 'IMPERSONATION_ORG_ID'
const UUID_LIKE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const App = observer(() => {
  const location = useLocation()

  const [isChangingOrganization, setIsChangingOrganization] = useState<boolean>(false)
  // Explicit open/closed choices per nav group; absent keys defer to whether
  // the group holds the active route.
  const [openNavGroups, setOpenNavGroups] = useState<Record<string, boolean>>({})

  // Appearance: light unless the user opted into dark; persisted per browser.
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      return localStorage.getItem('radomTheme') === 'dark' ? 'dark' : 'light'
    } catch {
      return 'light'
    }
  })

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem('radomTheme', theme)
    } catch {
      // Storage can be unavailable (private windows); the choice just won't stick.
    }
  }, [theme])
  const [numNotifications, setNumNotifications] = useState(0)
  const [notificationsCloseFn, setNotificationsCloseFn] = useState(() => () => { })
  const [orgDropdownCloseFn, setOrgDropdownCloseFn] = useState(() => () => { })

  const [isLoadingAckedNotifications, setIsLoadingAckedNotifications] = useState(true)
  const [latestManagedAck, setLatestManagedAck] = useState(new Date(0))

  const [organizationModalVisible, setOrganizationModalVisible] = useState(false)
  const [createOrganizationModalVisible, setCreateOrganizationModalVisible] = useState(false)
  const [organizationTransitionLabel, setOrganizationTransitionLabel] = useState('')
  const [organizations, setOrganizations] = useState([] as IOrganization[])
  const isUuidLike = (value?: string): boolean => !!value && UUID_LIKE_PATTERN.test(value.trim())
  const organizationDisplayName = (organization?: Pick<IOrganization, 'name' | 'organizationId'>): string => {
    const name = organization?.name?.trim()
    if (name && !isUuidLike(name)) return name
    const shortId = organization?.organizationId?.slice(0, 8)
    return shortId ? `New organization ${shortId}` : 'New organization'
  }
  const organizationIdPreview = (organization?: Pick<IOrganization, 'organizationId'>): string =>
    organization?.organizationId?.slice(0, 8) ?? 'Unavailable'
  const sortOrganizations = (organizationsToSort: IOrganization[]): IOrganization[] => [...organizationsToSort]
    .filter(o => !!o.organizationId)
    .sort((a, b) => {
      if (a.organizationId === User.selectedOrganizationId) return -1
      if (b.organizationId === User.selectedOrganizationId) return 1
      if (a.verificationStatus === 'Verified' && b.verificationStatus !== 'Verified') return -1
      if (b.verificationStatus === 'Verified' && a.verificationStatus !== 'Verified') return 1
      return organizationDisplayName(a).localeCompare(organizationDisplayName(b))
    })
  const uniqueOrganizations = Array.from(new Map(organizations.map(o => [o.organizationId, o])).values())
  const selectedOrg = uniqueOrganizations.find(o =>
    o.organizationId === User.selectedOrganizationId
  ) ?? User.organization
  const isQaPreview = new URLSearchParams(window.location.search).get('qa') === '1'
  const sortedOrganizations = sortOrganizations(uniqueOrganizations)
  const qaPreviewOrganizations = isQaPreview && selectedOrg?.name
    ? [
      selectedOrg,
      {
        organizationId: 'qa-preview-org-secondary',
        name: 'Radom Pay SP z. o.o.',
        verificationStatus: 'Verified'
      },
      {
        organizationId: 'qa-preview-org-tertiary',
        name: 'Radom Treasury Europe',
        verificationStatus: 'Verified'
      },
      {
        organizationId: 'qa-preview-org-fourth',
        name: 'Northstar Holdings Ltd',
        verificationStatus: 'Verified'
      },
      {
        organizationId: 'qa-preview-org-fifth',
        name: 'Keedsler Motors Europe',
        verificationStatus: 'Verified'
      },
      {
        organizationId: 'qa-preview-org-sixth',
        name: 'Radom Pay Limited',
        verificationStatus: 'Verified'
      }
    ] as IOrganization[]
    : []
  const isQaDropdownPreview = qaPreviewOrganizations.length > 0
  const organizationDropdownOrganizations = isQaDropdownPreview
    ? qaPreviewOrganizations
    : sortedOrganizations
  const organizationDropdownOrder = new Map(organizationDropdownOrganizations.map((organization, index) => [
    organization.organizationId,
    index
  ]))
  const currentVisibleOrganization = organizationDropdownOrganizations.find(o =>
    o.organizationId === User.selectedOrganizationId
  ) ?? selectedOrg
  const currentModalOrganization = currentVisibleOrganization
  const organizationsById = new Map(organizationDropdownOrganizations.map(organization => [
    organization.organizationId,
    organization
  ]))
  const childOrganizationsByParent = organizationDropdownOrganizations.reduce<Record<string, IOrganization[]>>(
    (acc, organization) => {
      if (!organization.parentOrganizationId) {
        return acc
      }

      if (!acc[organization.parentOrganizationId]) {
        acc[organization.parentOrganizationId] = []
      }

      acc[organization.parentOrganizationId].push(organization)
      return acc
    },
    {}
  )
  const orderOrganizationsForDisplay = (
    organizationsToOrder: IOrganization[]
  ): IOrganization[] => [...organizationsToOrder]
    .sort((a, b) => {
      const aIndex = organizationDropdownOrder.get(a.organizationId) ?? Number.MAX_SAFE_INTEGER
      const bIndex = organizationDropdownOrder.get(b.organizationId) ?? Number.MAX_SAFE_INTEGER
      return aIndex - bIndex
    })
  const getOrganizationParent = (organization?: IOrganization): IOrganization | undefined =>
    organization?.parentOrganizationId ? organizationsById.get(organization.parentOrganizationId) : undefined
  const getOrganizationChildCount = (organization?: IOrganization): number => {
    if (!organization?.organizationId) {
      return 0
    }

    return Math.max(
      childOrganizationsByParent[organization.organizationId]?.length ?? 0,
      organization.childOrganizationCount ?? 0
    )
  }
  const getOrganizationRelationLabel = (organization?: IOrganization): string | undefined => {
    if (!organization) {
      return undefined
    }

    if (organization.parentOrganizationId) {
      return 'Child organization'
    }

    if (getOrganizationChildCount(organization) > 0) {
      return 'Parent organization'
    }

    return undefined
  }
  const getOrganizationRelationHint = (organization?: IOrganization): string | undefined => {
    if (!organization) {
      return undefined
    }

    if (organization.parentOrganizationId) {
      const parentOrganization = getOrganizationParent(organization)
      const parentLabel = organizationDisplayName(parentOrganization ?? {
        name: organization.parentOrganizationName,
        organizationId: organization.parentOrganizationId
      })
      return `Child of ${parentLabel}`
    }

    const childCount = getOrganizationChildCount(organization)
    if (childCount > 0) {
      return `${childCount} child organization${childCount === 1 ? '' : 's'}`
    }

    return undefined
  }
  const organizationHierarchyRoots = orderOrganizationsForDisplay(
    organizationDropdownOrganizations.filter((organization) => {
      if (!organization.parentOrganizationId) {
        return true
      }

      return !organizationsById.has(organization.parentOrganizationId)
    })
  )
  const collectOrganizationDescendants = (organizationId?: string, visited = new Set<string>()): IOrganization[] => {
    if (!organizationId) {
      return []
    }

    const children = orderOrganizationsForDisplay(childOrganizationsByParent[organizationId] ?? [])
    const descendants: IOrganization[] = []

    for (const child of children) {
      if (!child.organizationId || visited.has(child.organizationId)) {
        continue
      }

      visited.add(child.organizationId)
      descendants.push(child)
      descendants.push(...collectOrganizationDescendants(child.organizationId, visited))
    }

    return descendants
  }
  const relatedOrganizationsMap = new Map<string, IOrganization>()
  const addRelatedOrganization = (organization?: IOrganization): void => {
    if (!organization?.organizationId || organization.organizationId === User.selectedOrganizationId) {
      return
    }

    if (!relatedOrganizationsMap.has(organization.organizationId)) {
      relatedOrganizationsMap.set(organization.organizationId, organization)
    }
  }

  addRelatedOrganization(getOrganizationParent(currentVisibleOrganization))
  if (currentVisibleOrganization?.parentOrganizationId) {
    orderOrganizationsForDisplay(childOrganizationsByParent[currentVisibleOrganization.parentOrganizationId] ?? [])
      .forEach(addRelatedOrganization)
  }
  collectOrganizationDescendants(currentVisibleOrganization?.organizationId).forEach(addRelatedOrganization)

  const fallbackOrganizations = organizationDropdownOrganizations.filter(organization =>
    organization.organizationId !== User.selectedOrganizationId &&
    !relatedOrganizationsMap.has(organization.organizationId)
  )
  const otherVisibleOrganizations = [
    ...Array.from(relatedOrganizationsMap.values()),
    ...fallbackOrganizations
  ].slice(0, isQaDropdownPreview ? 2 : 4)
  const hiddenOrganizationsCount = Math.max(
    0,
    organizationDropdownOrganizations.length - (currentVisibleOrganization ? 1 : 0) - otherVisibleOrganizations.length
  )

  const [testMode, setTestMode] = useTestMode()
  const canUseLiveMode = User.organization?.verificationStatus === 'Verified'
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false)

  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false)
  const [impersonationOrgId, setImpersonationOrgId] = useState('')
  const isImpersonatingOrganization = !!localStorage.getItem(IMPERSONATION_ORG_ID_KEY)
  const isActiveImpersonationSourceOrg = !!User.selectedOrganizationId &&
    IMPERSONATION_ALLOWED_ORG_IDS.has(User.selectedOrganizationId)
  const canShowImpersonationControls = isActiveImpersonationSourceOrg || isImpersonatingOrganization
  const handleImpersonationAction = (): void => {
    orgDropdownCloseFn()

    if (isImpersonatingOrganization) {
      localStorage.removeItem(IMPERSONATION_ORG_ID_KEY)
      window.location = '/' as any
      return
    }

    if (!isActiveImpersonationSourceOrg) {
      return
    }

    window.setTimeout(() => setImpersonationModalOpen(true), 0)
  }

  // The switcher list is one profile request per organization the user
  // belongs to. Firing them all at once, on every load and every click on
  // the switcher, queued the page's own requests behind dozens of profile
  // fetches. Load a few at a time and keep the result for a while.
  const ORGANIZATIONS_CACHE_MS = 5 * 60 * 1000
  const ORGANIZATION_FETCH_CONCURRENCY = 3
  const organizationsCache = useRef<{ loadedAt: number, orgs: IOrganization[] } | null>(null)
  const organizationsLoad = useRef<Promise<void> | null>(null)

  const loadOrganizations = async (): Promise<void> => {
    const cached = organizationsCache.current
    if (cached && Date.now() - cached.loadedAt < ORGANIZATIONS_CACHE_MS) {
      setOrganizations(cached.orgs)
      return
    }
    if (organizationsLoad.current) {
      return await organizationsLoad.current
    }

    organizationsLoad.current = (async () => {
      try {
        const orgIds = Array.from(new Set([
          ...(await Radom.listOrganizations()),
          User.selectedOrganizationId
        ].filter((orgId): orgId is string => !!orgId))).sort()

        const orgs: IOrganization[] = []
        for (let i = 0; i < orgIds.length; i += ORGANIZATION_FETCH_CONCURRENCY) {
          const batch = orgIds.slice(i, i + ORGANIZATION_FETCH_CONCURRENCY)
          orgs.push(...await Promise.all(batch.map(async o => await Radom.getOrganizationById(o))))
        }

        organizationsCache.current = { loadedAt: Date.now(), orgs }
        setOrganizations(orgs)
      } catch (err) {
        console.error('Failed to load organizations', err)
      } finally {
        organizationsLoad.current = null
      }
    })()
    return await organizationsLoad.current
  }

  const clearOrganizationTransitionState = (): void => {
    setIsChangingOrganization(false)
    setOrganizationTransitionLabel('')
  }

  const switchOrganization = async (organization: IOrganization): Promise<void> => {
    if (
      !organization.organizationId ||
      isChangingOrganization ||
      User.selectedOrganizationId === organization.organizationId ||
      organization.organizationId === 'qa-preview-org-secondary'
    ) {
      return
    }

    setOrganizationTransitionLabel(organizationDisplayName(organization))
    setOrganizationModalVisible(false)
    orgDropdownCloseFn()
    setIsChangingOrganization(true)

    try {
      await Radom.updateUser({ defaultOrganizationId: organization.organizationId })
    } catch (err: any) {
      clearOrganizationTransitionState()
      errorToast(err.response?.data?.error || err.message)
    }
  }

  const createOrganization = async (): Promise<void> => {
    if (isChangingOrganization) {
      return
    }

    setCreateOrganizationModalVisible(false)
    setOrganizationTransitionLabel('your new organization')
    orgDropdownCloseFn()
    setIsChangingOrganization(true)

    try {
      const { organizationId } = await Radom.createOrganization()
      await Radom.updateUser({ defaultOrganizationId: organizationId })
    } catch (err: any) {
      clearOrganizationTransitionState()
      errorToast(err.response?.data?.error || err.message)
    }
  }

  const copyOrganizationId = async (organization?: Pick<IOrganization, 'organizationId' | 'name'>): Promise<void> => {
    if (!organization?.organizationId) {
      return
    }

    try {
      await navigator.clipboard.writeText(organization.organizationId)
      successToast('Organization ID copied')
    } catch (err) {
      console.error('Failed to copy organization ID', err)
      errorToast('Failed to copy organization ID')
    }
  }

  const renderOrganizationRow = (
    organization: IOrganization,
    options: {
      context: 'dropdown' | 'modal'
      selected?: boolean
      nested?: boolean
      showId?: boolean
      allowCopy?: boolean
      disabled?: boolean
      onClick?: () => void
    }
  ): React.ReactElement => {
    const relationLabel = getOrganizationRelationLabel(organization)
    const relationHint = getOrganizationRelationHint(organization)
    const showId = options.context === 'modal' || options.showId
    const idLabel = showId
      ? organization.organizationId
      : organizationIdPreview(organization)
    const allowWrappedTitle = options.context === 'modal' || options.showId

    return <OrganizationRowShell>
      <OrganizationRowButton
        role={options.onClick ? 'button' : undefined}
        tabIndex={options.disabled || !options.onClick ? -1 : 0}
        aria-disabled={options.onClick ? options.disabled : undefined}
        onClick={options.disabled ? undefined : options.onClick}
        onKeyDown={options.disabled || !options.onClick
          ? undefined
          : (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              options.onClick?.()
            }
          }}
        $context={options.context}
        $nested={options.nested}
        $disabled={options.disabled}
        $selected={options.selected}>
        <OrganizationRowText>
          <OrganizationRowTitleLine>
            <OrganizationRowTitle $context={options.context} $allowWrap={allowWrappedTitle}>
              {organizationDisplayName(organization)}
            </OrganizationRowTitle>
            {options.context === 'modal' && relationLabel && (
              <OrganizationRelationLabel>{relationLabel}</OrganizationRelationLabel>
            )}
          </OrganizationRowTitleLine>
          {showId && (
            <OrganizationRowMeta>
              <OrganizationRowId $context={options.context}>{idLabel}</OrganizationRowId>
              {options.allowCopy && organization.organizationId && (
                <OrganizationInlineCopyButton
                  type='button'
                  aria-label={`Copy organization ID for ${organizationDisplayName(organization)}`}
                  onClick={async (e) => {
                    e.stopPropagation()
                    await copyOrganizationId(organization)
                  }}>
                  <Copy width={14} height={14} style={{ stroke: 'currentColor' }} />
                </OrganizationInlineCopyButton>
              )}
            </OrganizationRowMeta>
          )}
          {options.context === 'modal' && relationHint && (
            <OrganizationRowHint>{relationHint}</OrganizationRowHint>
          )}
        </OrganizationRowText>
        <OrganizationRowTrailing>
          {options.selected
            ? options.context === 'modal'
              ? <SelectedTag>Selected</SelectedTag>
              : <OrganizationTick>
                <Checkmark width={12} height={12} style={{ stroke: RADOM_COLORS.BLACK }} />
              </OrganizationTick>
            : null}
        </OrganizationRowTrailing>
      </OrganizationRowButton>
    </OrganizationRowShell>
  }

  const renderOrganizationTree = (organization: IOrganization, depth = 0): React.ReactElement => {
    const childOrganizations = orderOrganizationsForDisplay(
      childOrganizationsByParent[organization.organizationId] ?? []
    )
    const isSelected = organization.organizationId === User.selectedOrganizationId

    return <OrganizationTreeGroup key={organization.organizationId}>
      {renderOrganizationRow(organization, {
        context: 'modal',
        selected: isSelected,
        nested: depth > 0,
        allowCopy: true,
        disabled: isChangingOrganization || organization.organizationId === 'qa-preview-org-secondary',
        onClick: isSelected
          ? () => setOrganizationModalVisible(false)
          : () => {
            void switchOrganization(organization)
          }
      })}
      {childOrganizations.length > 0 && (
        <OrganizationChildren>
          {childOrganizations.map(childOrganization => renderOrganizationTree(childOrganization, depth + 1))}
        </OrganizationChildren>
      )}
    </OrganizationTreeGroup>
  }

  const onClickNotifications = async (): Promise<void> => {
    Radom.updateLastBlockAck([
      { managedLastAck: new Date().toISOString() }
    ]).then(() => {
      setNumNotifications(0)
    })
  }

  const loadUserNotifications = async (): Promise<void> => {
    setIsLoadingAckedNotifications(true)
    Radom.getLastBlockAck()
      .then(acks => {
        for (const ack of acks) {
          if (ack.managedLastAck) {
            setLatestManagedAck(new Date(ack.managedLastAck))
          }
        }
      })
      .catch(() => { })
      .finally(() => {
        setIsLoadingAckedNotifications(false)
      })
  }

  useEffect(() => {
    if (User.organization && User.organization.verificationStatus !== 'Verified') {
      setTestMode(true)
    }
  }, [User.organization])

  useEffect(() => {
    if (isLoadingAckedNotifications) return
    setNumNotifications(EventsState.managedEvents.filter(e => e.date.getTime() > latestManagedAck.getTime()).length)
  }, [EventsState.allEvents, latestManagedAck])

  useEffect(() => {
    if (!Radom.isLoggedIn) {
      return
    }
    void User.getUser()
    void User.getOrganization()
    if (!User.permissionsLoaded) {
      return
    }

    if (User.hasPermission('ReadEvents')) {
      EventsState.loadEvents(testMode)
    }
    if (User.hasPermission('ReadBalance')) {
      Treasury.loadBalances()
    }
    loadOrganizations()
    loadUserNotifications()
  }, [Radom.isLoggedIn, User.selectedOrganizationId, User.permissionsLoaded])

  useEffect(() => {
    if (!Radom.isLoggedIn) {
      return
    }

    Radom.refreshSupportUnreadCount()
    Radom.refreshReportsUnreadCount()
    const interval = setInterval(() => {
      Radom.refreshSupportUnreadCount()
      Radom.refreshReportsUnreadCount()
    }, 60_000)
    return () => clearInterval(interval)
  }, [Radom.isLoggedIn, User.selectedOrganizationId])

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth <= 900)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!isMobile) {
      setIsMobileNavOpen(false)
    }
  }, [isMobile])

  // The top bar is fixed and the spacer under it is a constant that falls
  // short of the bar's real two-row height on mobile. Measure the bar so the
  // announcement banner can start exactly under it without moving any page.
  const topBarRef = useRef<HTMLDivElement>(null)
  const [topBarHeight, setTopBarHeight] = useState(0)
  useEffect(() => {
    const element = topBarRef.current
    if (!element) {
      setTopBarHeight(0)
      return
    }
    const measure = (): void => setTopBarHeight(element.getBoundingClientRect().height)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [NavbarController.isFullscreen])

  const navIsHidden = NavbarController.isFullscreen || (isMobile && !isMobileNavOpen)
  const contentMarginLeft = NavbarController.isFullscreen || isMobile ? 0 : 228
  const topNavHeight = isMobile ? 108 : 66
  // Highlight from the URL rather than click state, so deep links and
  // in-page navigation keep the sidebar honest.
  const isNavRouteActive = (path?: string): boolean => {
    if (!path) return false
    if (path === '/') return location.pathname === '/' || !location.pathname
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  return (
    <div style={{
      margin: 'auto'
    }}>
      <GlobalStyles
        isModalOpen={NavbarController.isModalOpen ||
          (isMobile && isMobileNavOpen) ||
          isChangingOrganization} />
      <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
        <WithAuthentication>
          <div style={{
            display: 'flex',
            flexDirection: 'row'
          }}>

            {isMobile && isMobileNavOpen && <MobileOverlay onClick={() => setIsMobileNavOpen(false)} />}

            <RadomNavContainer $isHidden={navIsHidden} $isMobile={isMobile}>
              <RadomNav>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                  height: '100%',
                  minHeight: 0
                }}>

                  <div style={{
                    padding: '12px 18px 10px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 10
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <NavRadomLogo to="/" onClick={() => {
                        if (isMobile) {
                          setIsMobileNavOpen(false)
                        }
                      }}>
                        <RadomLogo style={{ width: 70 }} />
                      </NavRadomLogo>

                      {isMobile &&
                        <IconButton
                          aria-label='Close navigation'
                          onClick={() => setIsMobileNavOpen(false)}
                          style={{ padding: 10 }}>
                          <Close width={18} height={18} />
                        </IconButton>
                      }
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px 10px 12px',
                    width: '100%',
                    gap: 5,
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}>
                    {routes.filter(r => isRouteAccessible(r, testMode, selectedOrg))
                      .map(route => route.children
                        ? { ...route, children: route.children.filter(c => satisfiesPermission(c.permission)) }
                        : route)
                      .filter(route => route.children
                        ? route.children.length > 0
                        : satisfiesPermission(route.permission))
                      .filter(r => r.name).map((route, index, visibleRoutes) => {
                        if (route.isBottomNav) return null

                        const previous = visibleRoutes[index - 1]
                        const startsSection = !!route.section && route.section !== previous?.section

                        if (route.children) {
                          const groupName = route.name ?? ''
                          const childActive = route.children.some(child => isNavRouteActive(child.path))
                          // A group with an active child stays open until the
                          // user explicitly collapses it; otherwise groups
                          // rest closed to keep the nav short.
                          const open = openNavGroups[groupName] ?? childActive
                          const toggle = (): void => {
                            setOpenNavGroups(prev => ({ ...prev, [groupName]: !open }))
                          }

                          return <React.Fragment key={groupName}>
                            {route.dividerAbove && <NavDivider />}
                            {startsSection && <NavSectionLabel>{route.section}</NavSectionLabel>}
                            <NavGroupHeader
                              $active={childActive && !open}
                              $open={open}
                              role='button'
                              tabIndex={0}
                              aria-expanded={open}
                              onClick={toggle}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  toggle()
                                }
                              }}>
                              <div style={{ alignItems: 'center', display: 'flex', gap: 7 }}>
                                {route.icon && <route.icon style={{ width: 16, height: 16, flex: '0 0 auto' }} />}
                                <span>{route.name}</span>
                              </div>
                              <Chevron id='chevron' fill='currentColor' />
                            </NavGroupHeader>
                            <NavGroupChildren $open={open}>
                              {route.children.map(child => {
                                const childIsActive = isNavRouteActive(child.path)
                                return <NavItem
                                  key={child.name}
                                  $$active={childIsActive}
                                  onClick={() => {
                                    if (isMobile) {
                                      setIsMobileNavOpen(false)
                                    }
                                  }}
                                  to={child.path ?? ''}>
                                  <NavItemText
                                    style={{ fontSize: 13.25, paddingLeft: 15 }}
                                    $active={childIsActive}>
                                    {child.icon && <child.icon style={{ width: 15, height: 15, flex: '0 0 auto' }} />}
                                    <span>{child.name}</span>
                                  </NavItemText>
                                </NavItem>
                              })}
                            </NavGroupChildren>
                          </React.Fragment>
                        }

                        const active = isNavRouteActive(route.path)

                        return <React.Fragment key={route.name}>
                          {route.dividerAbove && <NavDivider />}
                          {startsSection && <NavSectionLabel>{route.section}</NavSectionLabel>}
                          <NavItem
                            $$active={active}
                            onClick={() => {
                              if (isMobile) {
                                setIsMobileNavOpen(false)
                              }
                            }}
                            to={route.path ?? ''}>
                            <NavItemText $active={active}>
                              {route.icon && <route.icon style={{ width: 16, height: 16, flex: '0 0 auto' }} />}
                              <span>{route.name}</span>
                              {route.name === 'Statements' && Radom.reportsUnreadCount > 0 &&
                                <UnreadPip title={`${Radom.reportsUnreadCount} new`}>
                                  {Radom.reportsUnreadCount > 9 ? '9+' : Radom.reportsUnreadCount}
                                </UnreadPip>}
                            </NavItemText>
                          </NavItem>
                        </React.Fragment>
                      })}
                  </div>
                </div>

              </RadomNav>
              <RadomBottomNav>
                {
                  routes.filter(r => r.isBottomNav).map(route => {
                    if (route.isExternal) {
                      return <a
                        key={route.name}
                        href={route.path}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ textDecoration: 'none', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        onClick={() => {
                          if (isMobile) setIsMobileNavOpen(false)
                        }}
                      >
                        <NavItemText $active={false}>
                          {route.icon && <route.icon style={{ width: 15 }} />}
                          <span>{route.name}</span>
                        </NavItemText>
                      </a>
                    }

                    return <NavItem
                      key={route.name}
                      $$active={isNavRouteActive(route.path)}
                      onClick={() => {
                        if (isMobile) {
                          setIsMobileNavOpen(false)
                        }
                      }}
                      to={route.path ?? ''}
                    >
                      <NavItemText $active={isNavRouteActive(route.path)}>
                        {route.icon && <route.icon style={{ width: 15 }} />}
                        <span>{route.name}</span>
                        {route.name === 'Support' && Radom.supportUnreadCount > 0 &&
                          <UnreadPip>{Radom.supportUnreadCount > 9 ? '9+' : Radom.supportUnreadCount}</UnreadPip>}
                      </NavItemText>
                    </NavItem>
                  })
                }
              </RadomBottomNav>
            </RadomNavContainer>

            <div style={{
              marginLeft: contentMarginLeft,
              transition: '0.2s ease all',
              width: `calc(100% - ${contentMarginLeft}px)`,
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'clip',
              minHeight: '100vh'
            }}>
              <div style={{ width: '100%' }}>

                {!NavbarController.isFullscreen && <>
                  <div style={{ height: topNavHeight, width: '100%' }} />
                  <div ref={topBarRef} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 4,
                    pointerEvents: 'none'
                  }}>
                    <div style={{
                      width: '100%',
                      margin: '0 auto'
                    }}>
                      <div style={{
                        marginLeft: contentMarginLeft,
                        display: 'flex',
                        alignItems: isMobile ? 'normal' : 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 14,
                        background: 'var(--surface, #ffffff)',
                        borderBottom: '1px solid var(--surface-pressed, #edf1f7)',
                        padding: '10px 18px',
                        pointerEvents: 'auto'
                      }}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}
                        >

                          {isMobile &&
                          <IconButton
                            aria-label='Open navigation'
                            onClick={() => setIsMobileNavOpen(true)}
                            style={{ padding: 10, display: 'flex', alignItems: 'center' }}>
                            <Menu width={18} height={18} />
                          </IconButton>
                          }

                          <SearchBox />
                        </div>

                        <div style={{ display: 'flex', height: '100%', alignItems: 'center', gap: 6, marginLeft: isMobile ? 0 : 'auto', alignSelf: isMobile ? 'flex-end' : 'normal' }}>
                          <HelpDropdown notificationsCloseFn={() => {}}
                            onClickHelpIcon={() => {}}
                            setNotificationsCloseFn={() => {}}
                          />
                          <NotificationDropdown onClickNotifications={onClickNotifications}
                            latestManagedAck={latestManagedAck}
                            setLatestManagedAck={setLatestManagedAck}
                            notificationsCloseFn={notificationsCloseFn}
                            numNotifications={numNotifications}
                            setNotificationsCloseFn={setNotificationsCloseFn}
                          />
                          <IconButton
                            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                            onClick={() => setTheme(current => current === 'dark' ? 'light' : 'dark')}
                            style={{ height: 44, minWidth: 42, padding: '8px 12px' }}>
                            <ThemeGlyph dark={theme === 'dark'} />
                          </IconButton>

                          <div style={{
                            width: 1,
                            height: 22,
                            background: 'var(--border, #e3e8f1)',
                            margin: '0 6px 0 8px'
                          }} />

                          <Dropdown
                            onClick={() => {
                              loadOrganizations()
                            }}
                            overflow='hidden'
                            disabled={isChangingOrganization}
                            preferredAlignment='right'
                            onCloseFn={fn => setOrgDropdownCloseFn(() => fn)}
                            containerStyle={{ minWidth: 220, maxWidth: 'min(240px, 100%)' }}
                            outerStyle={{
                              background: isChangingOrganization ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface, #ffffff)',
                              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                              padding: '7px 11px 7px 12px',
                              borderRadius: 14,
                              gap: 10,
                              minHeight: 44,
                              marginLeft: 8
                            }}
                            innerStyle={{
                              width: 'min(360px, calc(100vw - 24px))',
                              maxHeight: 'min(calc(100vh - 88px), 620px)',
                              borderRadius: 16,
                              border: '1px solid var(--border, #e7eaf0)',
                              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)'
                            }}
                            selectedContent={
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 2,
                                minWidth: 0
                              }}>
                                <span style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  letterSpacing: '0.05em',
                                  textTransform: 'uppercase',
                                  color: 'var(--text-faint, rgba(17, 17, 17, 0.45))'
                                }}>
                            Organization
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                                  {isChangingOrganization
                                    ? <Spinner style={{ width: 14, height: 14 }} />
                                    : null}
                                  <span style={{
                                    maxWidth: 138,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: RADOM_COLORS.BLACK,
                                    fontSize: 13,
                                    fontWeight: 500
                                  }}>
                                    {isChangingOrganization && organizationTransitionLabel
                                      ? organizationTransitionLabel
                                      : organizationDisplayName(selectedOrg)}
                                  </span>
                                  <ModePill $test={testMode}>{testMode ? 'Test' : 'Live'}</ModePill>
                                </div>
                              </div>
                            }
                            dropdownContent={
                              <OrganizationDropdownContent>
                                {
                                  User._user && <>
                                    <OrganizationDropdownBody>
                                      {currentVisibleOrganization && <>
                                        <div style={{ padding: '0 12px' }}>
                                          <OrganizationSectionLabel>Current</OrganizationSectionLabel>
                                        </div>
                                        <div style={{ padding: '0 8px' }}>
                                          {renderOrganizationRow(currentVisibleOrganization, {
                                            context: 'dropdown',
                                            selected: true,
                                            showId: true,
                                            allowCopy: true,
                                            disabled: isChangingOrganization
                                          })}
                                        </div>
                                      </>}

                                      {otherVisibleOrganizations.length > 0 && <>
                                        <OrganizationSectionDivider />
                                        <div style={{ padding: '0 12px' }}>
                                          <OrganizationSectionLabel>Related organizations</OrganizationSectionLabel>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 8px' }}>
                                          {otherVisibleOrganizations.map(o =>
                                            <div key={o.organizationId}>
                                              {renderOrganizationRow(o, {
                                                context: 'dropdown',
                                                disabled: isChangingOrganization || o.organizationId === 'qa-preview-org-secondary',
                                                onClick: () => {
                                                  void switchOrganization(o)
                                                }
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </>}

                                      <OrganizationDropdownActions>
                                        <DropdownItem
                                          onClick={() => {
                                            orgDropdownCloseFn()
                                            window.setTimeout(() => setOrganizationModalVisible(true), 0)
                                          }}
                                          style={{
                                            width: '100%',
                                            padding: '11px 12px',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 10,
                                            color: RADOM_COLORS.GRAY_DARKEST,
                                            cursor: 'pointer',
                                            borderRadius: 12
                                          }}>
                                          <span style={{
                                            fontSize: 13,
                                            lineHeight: 1,
                                            opacity: 0.6,
                                            letterSpacing: 0.6,
                                            paddingTop: 3
                                          }}>•••</span>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <OrganizationDropdownActionLabel>
                                              View all organizations
                                            </OrganizationDropdownActionLabel>
                                            <OrganizationDropdownActionHint>
                                              {hiddenOrganizationsCount > 0
                                                ? `Browse the full hierarchy and ${hiddenOrganizationsCount} more ` +
                                                  `organization${hiddenOrganizationsCount === 1 ? '' : 's'}.`
                                                : 'Browse the full hierarchy and switch merchant context.'}
                                            </OrganizationDropdownActionHint>
                                          </div>
                                        </DropdownItem>

                                        <DropdownItem
                                          onClick={() => {
                                            orgDropdownCloseFn()
                                            window.setTimeout(() => setCreateOrganizationModalVisible(true), 0)
                                          }}
                                          style={{
                                            width: '100%',
                                            gap: 10,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            padding: '11px 12px',
                                            color: RADOM_COLORS.BLACK,
                                            borderRadius: 12
                                          }}>
                                          <span style={{
                                            fontSize: 13,
                                            lineHeight: 1,
                                            color: RADOM_COLORS.GRAY_DARKER,
                                            paddingTop: 3
                                          }}>+</span>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <OrganizationDropdownActionLabel>
                                              Create new organization
                                            </OrganizationDropdownActionLabel>
                                            <OrganizationDropdownActionHint>
                                              Start a new organization workspace and switch into it.
                                            </OrganizationDropdownActionHint>
                                          </div>
                                        </DropdownItem>
                                      </OrganizationDropdownActions>
                                    </OrganizationDropdownBody>

                                    <OrganizationDropdownFooter>
                                      <div style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 10,
                                        color: RADOM_COLORS.BLACK,
                                        padding: '2px 6px 0'
                                      }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ fontSize: 13, fontWeight: 400 }}>Mode</span>
                                            <Tooltip message={
                                              canUseLiveMode
                                                ? 'Switch between test and live data for the dashboard.'
                                                : 'To enable live mode, please verify your organization.'
                                            }>
                                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <HoverInfoIcon width={15} height={15} />
                                              </div>
                                            </Tooltip>
                                          </div>
                                          <span style={{ fontSize: 11.5, color: 'var(--text-faint, #8a91a0)', lineHeight: 1.35 }}>
                                            {testMode ? 'Sandbox balances, payouts and accounts' : 'Production balances, payouts and accounts'}
                                          </span>
                                        </div>
                                        <ModeToggle>
                                          <ModeToggleButton
                                            type='button'
                                            $active={!testMode}
                                            disabled={!canUseLiveMode}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              if (canUseLiveMode) {
                                                setTestMode(false)
                                              }
                                            }}>
                                      Live
                                          </ModeToggleButton>
                                          <ModeToggleButton
                                            type='button'
                                            $active={testMode}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              if (!testMode) {
                                                setTestMode(true)
                                              }
                                            }}>
                                      Test
                                          </ModeToggleButton>
                                        </ModeToggle>
                                      </div>

                                      {canShowImpersonationControls && <DropdownItem
                                        onClick={handleImpersonationAction}
                                        style={{
                                          width: '100%',
                                          margin: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: RADOM_COLORS.BLACK,
                                          padding: '10px 12px',
                                          borderRadius: 12,
                                          border: '1px solid var(--border, #e7eaf0)',
                                          background: 'var(--surface, #ffffff)'
                                        }}>
                                        <span style={{ fontSize: 13.75, fontWeight: 520 }}>
                                          {isImpersonatingOrganization ? 'Stop impersonating' : 'Impersonate Org'}
                                        </span>
                                      </DropdownItem>}

                                      <DropdownItem
                                        onClick={() => {
                                          orgDropdownCloseFn()
                                          window.setTimeout(() => {
                                            Radom.signOut()
                                          }, 0)
                                        }}
                                        style={{
                                          width: '100%',
                                          margin: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: RADOM_COLORS.BLACK,
                                          padding: '10px 12px',
                                          borderRadius: 12,
                                          border: '1px solid var(--border, #e7eaf0)',
                                          background: 'var(--surface-hover, #f7f8fa)'
                                        }}>
                                        <span style={{ fontSize: 13.75, fontWeight: 520 }}>Sign out</span>
                                      </DropdownItem>
                                    </OrganizationDropdownFooter>
                                  </>
                                }
                              </OrganizationDropdownContent>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>}

                {/* Announcement for organizations moved onto the unified treasury; sits under the top bar on every route. */}
                {!NavbarController.isFullscreen &&
                  <RadomV2Banner offsetTop={Math.max(0, Math.ceil(topBarHeight - topNavHeight))} />}

                <Routes>
                  {
                    routes.
                    filter(r => isRouteAccessible(r, testMode, selectedOrg))
                    .map((route, index) => <Route
                      key={index}
                      path={route.path}
                      element={
                        <RequirePermission permission={route.path ? routePermissions.get(route.path) : undefined}>
                          {route.element}
                        </RequirePermission>
                      }
                    />
                    )
                  }
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <InviteAcceptModal />
                <ConvertDrawer />
                <OpenBankingPaymentDrawer />
                {
                  isChangingOrganization &&
                  <AppBlocker>
                    <AppBlockerCard>
                      <Spinner style={{ width: 24, height: 24 }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: RADOM_COLORS.BLACK }}>
                          Switching organization
                        </span>
                        <span style={{ fontSize: 13.5, color: RADOM_COLORS.GRAY_DARKEST, lineHeight: 1.45 }}>
                          {organizationTransitionLabel
                            ? `Loading ${organizationTransitionLabel}. This can take a moment.`
                            : 'Loading the selected organization. This can take a moment.'}
                        </span>
                      </div>
                    </AppBlockerCard>
                  </AppBlocker>
                }
              </div>
            </div>

          </div>

          <Modal
            visible={organizationModalVisible}
            onClose={() => setOrganizationModalVisible(false)}
            title='Select organization'
            innerStyles={{ width: 'min(680px, calc(100vw - 40px))' }}>

            <div style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              fontSize: 14,
              gap: 10
            }}>
              {isChangingOrganization && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.6)',
                    zIndex: 10
                  }}
                >
                  <Spinner />
                </div>
              )}
              <div style={{ color: RADOM_COLORS.GRAY_DARKEST, fontSize: 13.25, lineHeight: 1.5, maxWidth: 540 }}>
                Switch dashboard organization.
              </div>

              {currentModalOrganization && <div>
                <OrganizationSectionLabel>Current</OrganizationSectionLabel>
                <OrganizationModalList>
                  {renderOrganizationRow(currentModalOrganization, {
                    context: 'modal',
                    selected: true,
                    allowCopy: true,
                    disabled: isChangingOrganization,
                    onClick: () => setOrganizationModalVisible(false)
                  })}
                </OrganizationModalList>
              </div>}

              {organizationHierarchyRoots.length > 0 && <div>
                <OrganizationSectionLabel>Organization hierarchy</OrganizationSectionLabel>
                <OrganizationModalList>
                  {organizationHierarchyRoots.map(organization => renderOrganizationTree(organization))}
                </OrganizationModalList>
              </div>}
            </div>

          </Modal>

          <Modal
            visible={createOrganizationModalVisible}
            onClose={() => setCreateOrganizationModalVisible(false)}
            title='Create organization'
            innerStyles={{ width: '420px', minHeight: 'auto' }}>
            <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST, lineHeight: 1.6 }}>
              Create a new organization and switch into it right away.
            </span>
            <ModalFooter $fullWidthAction>
              <SecondaryButton
                type='button'
                disabled={isChangingOrganization}
                onClick={() => setCreateOrganizationModalVisible(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type='button'
                disabled={isChangingOrganization}
                onClick={createOrganization}>
                Create organization
              </PrimaryButton>
            </ModalFooter>
          </Modal>

          <Modal
            title='Impersonate organization'
            visible={impersonationModalOpen && isActiveImpersonationSourceOrg}
            onClose={() => setImpersonationModalOpen(false)}>
            <div style={{ display: 'flex', padding: '0 30px 30px 30px', width: '100%' }}>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: '100%' }}>
                <InputLabel style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>
                  Organization ID
                </InputLabel>
                <TextInput
                  placeholder='00000000-0000-0000-0000-000000000000'
                  value={impersonationOrgId}
                  onChange={e => {
                    setImpersonationOrgId(e.target.value)
                  }} />
              </div>
            </div>

            <div style={{ padding: '0 30px 30px 30px' }}>
              <PrimaryButton
                style={{ width: '100%' }}
                disabled={!impersonationOrgId || !isActiveImpersonationSourceOrg}
                onClick={() => {
                  if (!isActiveImpersonationSourceOrg) {
                    return
                  }

                  localStorage.setItem(IMPERSONATION_ORG_ID_KEY, impersonationOrgId)
                  window.location.reload()
                }}>
                Impersonate
              </PrimaryButton>
            </div>
          </Modal>
        </WithAuthentication>
        <Toaster position="bottom-right" />
      </GoogleOAuthProvider>
    </div>
  )
})

export default App
